/**
 * Competencies Settings Tab
 *
 * Manage competencies with CRUD operations, toggle active status,
 * and support for both basis (general) and vak (task-specific) competencies.
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { competenciesApi, type Competency, type CreateCompetencyInput, type UpdateCompetencyInput } from '../../../lib/competenciesApi';
import { tasksApi, type TaskListItem } from '../../../lib/tasksApi';
import { matchesSearch } from '../../../lib/textUtils';

type TypeFilter = 'all' | 'basis' | 'vak';
type StatusFilter = 'all' | 'active' | 'inactive';

interface CompetencyFormData {
  name: string;
  beschrijving: string;
  type: 'basis' | 'vak';
  examples: string;
  taskIds: number[];
}

interface DeleteError {
  code: string;
  error: string;
  employeeCount?: number;
  taskCount?: number;
}

export function CompetenciesSettingsTab() {
  const queryClient = useQueryClient();

  // Data fetching
  const { data: competencies, isLoading, error } = useQuery({
    queryKey: ['competencies'],
    queryFn: () => competenciesApi.getAll(),
  });

  const { data: tasks } = useQuery({
    queryKey: ['tasks'],
    queryFn: () => tasksApi.list(),
  });

  // Mutations
  const createCompetency = useMutation({
    mutationFn: (data: CreateCompetencyInput) => competenciesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['competencies'] });
    },
  });

  const updateCompetency = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateCompetencyInput }) => competenciesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['competencies'] });
    },
  });

  const toggleActive = useMutation({
    mutationFn: (id: number) => competenciesApi.toggleActive(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['competencies'] });
    },
    onError: (error: DeleteError) => {
      setToggleError(error);
    },
  });

  const deleteCompetency = useMutation({
    mutationFn: (id: number) => competenciesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['competencies'] });
      setDeleteModalState({ isOpen: false, competency: null, error: null });
    },
    onError: (error: DeleteError) => {
      setDeleteModalState(prev => ({ ...prev, error }));
    },
  });

  // Local state
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingCompetency, setEditingCompetency] = useState<Competency | null>(null);
  const [formData, setFormData] = useState<CompetencyFormData>({
    name: '',
    beschrijving: '',
    type: 'basis',
    examples: '',
    taskIds: [],
  });
  const [deleteModalState, setDeleteModalState] = useState<{
    isOpen: boolean;
    competency: Competency | null;
    error: DeleteError | null;
  }>({ isOpen: false, competency: null, error: null });
  const [toggleError, setToggleError] = useState<DeleteError | null>(null);

  // Filter competencies (accent-insensitive)
  const filteredCompetencies = competencies?.filter(comp => {
    const matchesTypeFilter =
      typeFilter === 'all' ||
      comp.type === typeFilter;
    const matchesStatusFilter =
      statusFilter === 'all' ||
      (statusFilter === 'active' && comp.isActive) ||
      (statusFilter === 'inactive' && !comp.isActive);
    const matchesSearchFilter = matchesSearch(comp.name, searchQuery) ||
      matchesSearch(comp.beschrijving ?? '', searchQuery);
    return matchesTypeFilter && matchesStatusFilter && matchesSearchFilter;
  }) ?? [];

  // Get active tasks for vak competency linking
  const activeTasks = tasks?.data.filter(t => t.isActief) ?? [];

  // Form handlers
  const resetForm = () => {
    setFormData({ name: '', beschrijving: '', type: 'basis', examples: '', taskIds: [] });
    setEditingCompetency(null);
    setIsFormOpen(false);
  };

  const handleEdit = (comp: Competency) => {
    setEditingCompetency(comp);
    setFormData({
      name: comp.name,
      beschrijving: comp.beschrijving || '',
      type: comp.type,
      examples: comp.examples || '',
      taskIds: comp.linkedTasks.map(t => t.id),
    });
    setIsFormOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (editingCompetency) {
      await updateCompetency.mutateAsync({
        id: editingCompetency.id,
        data: {
          name: formData.name,
          beschrijving: formData.beschrijving || undefined,
          examples: formData.examples || undefined,
          taskIds: formData.type === 'vak' ? formData.taskIds : undefined,
        },
      });
    } else {
      await createCompetency.mutateAsync({
        name: formData.name,
        beschrijving: formData.beschrijving || undefined,
        type: formData.type,
        examples: formData.examples || undefined,
        taskIds: formData.type === 'vak' ? formData.taskIds : undefined,
      });
    }
    resetForm();
  };

  const handleDeleteClick = (comp: Competency) => {
    setDeleteModalState({ isOpen: true, competency: comp, error: null });
  };

  const handleConfirmDelete = async () => {
    if (deleteModalState.competency) {
      try {
        await deleteCompetency.mutateAsync(deleteModalState.competency.id);
      } catch {
        // Error handled in onError callback
      }
    }
  };

  const handleToggleActive = async (id: number) => {
    setToggleError(null);
    try {
      await toggleActive.mutateAsync(id);
    } catch {
      // Error handled in onError callback
    }
  };

  const handleTaskToggle = (taskId: number) => {
    setFormData(prev => ({
      ...prev,
      taskIds: prev.taskIds.includes(taskId)
        ? prev.taskIds.filter(id => id !== taskId)
        : [...prev.taskIds, taskId],
    }));
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Laden...</span>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-sm text-red-700">Fout bij het laden van competenties.</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-lg font-medium text-gray-900">Competenties</h2>
          <p className="text-sm text-gray-500">
            Beheer basis- en vakcompetenties voor medewerkers
          </p>
        </div>
        <button
          onClick={() => setIsFormOpen(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
        >
          <svg className="mr-2 -ml-1 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Nieuwe Competentie
        </button>
      </div>

      {/* Toggle Error Alert */}
      {toggleError && (
        <div className="mb-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex">
            <svg className="h-5 w-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">{toggleError.error}</p>
              <button
                onClick={() => setToggleError(null)}
                className="mt-2 text-sm text-yellow-600 underline hover:text-yellow-800"
              >
                Sluiten
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-4 mb-4">
        <div>
          <label htmlFor="type-filter" className="sr-only">Type filter</label>
          <select
            id="type-filter"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as TypeFilter)}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
          >
            <option value="all">Alle types</option>
            <option value="basis">Basiscompetenties</option>
            <option value="vak">Vakcompetenties</option>
          </select>
        </div>
        <div>
          <label htmlFor="status-filter" className="sr-only">Status filter</label>
          <select
            id="status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
          >
            <option value="all">Alle statussen</option>
            <option value="active">Actief</option>
            <option value="inactive">Inactief</option>
          </select>
        </div>
        <div className="flex-1">
          <label htmlFor="search" className="sr-only">Zoeken</label>
          <input
            type="text"
            id="search"
            placeholder="Zoeken op naam of beschrijving..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
          />
        </div>
      </div>

      {/* Form Modal */}
      {isFormOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500/75 transition-opacity" onClick={resetForm}></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="relative z-10 inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <form onSubmit={handleSubmit}>
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  {editingCompetency ? 'Competentie Bewerken' : 'Nieuwe Competentie'}
                </h3>

                <div className="space-y-4">
                  {/* Name */}
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                      Naam *
                    </label>
                    <input
                      type="text"
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      required
                      maxLength={100}
                    />
                  </div>

                  {/* Type - only for new competencies */}
                  {!editingCompetency && (
                    <div>
                      <label htmlFor="type" className="block text-sm font-medium text-gray-700">
                        Type *
                      </label>
                      <select
                        id="type"
                        value={formData.type}
                        onChange={(e) => setFormData({ ...formData, type: e.target.value as 'basis' | 'vak', taskIds: [] })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                        required
                      >
                        <option value="basis">Basiscompetentie</option>
                        <option value="vak">Vakcompetentie</option>
                      </select>
                      <p className="mt-1 text-xs text-gray-500">
                        {formData.type === 'basis'
                          ? 'Basiscompetenties worden handmatig toegewezen aan medewerkers'
                          : 'Vakcompetenties worden automatisch toegewezen via taken'}
                      </p>
                    </div>
                  )}

                  {/* Type display for editing */}
                  {editingCompetency && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Type
                      </label>
                      <p className="mt-1 text-sm text-gray-900">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          formData.type === 'basis' ? 'bg-purple-100 text-purple-800' : 'bg-indigo-100 text-indigo-800'
                        }`}>
                          {formData.type === 'basis' ? 'Basis' : 'Vak'}
                        </span>
                      </p>
                      <p className="mt-1 text-xs text-gray-500">
                        Type kan niet worden gewijzigd na aanmaken
                      </p>
                    </div>
                  )}

                  {/* Description */}
                  <div>
                    <label htmlFor="beschrijving" className="block text-sm font-medium text-gray-700">
                      Beschrijving
                    </label>
                    <textarea
                      id="beschrijving"
                      rows={3}
                      value={formData.beschrijving}
                      onChange={(e) => setFormData({ ...formData, beschrijving: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      maxLength={500}
                    />
                  </div>

                  {/* Examples */}
                  <div>
                    <label htmlFor="examples" className="block text-sm font-medium text-gray-700">
                      Voorbeelden
                    </label>
                    <textarea
                      id="examples"
                      rows={2}
                      value={formData.examples}
                      onChange={(e) => setFormData({ ...formData, examples: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      placeholder="Voorbeelden van hoe deze competentie wordt gedemonstreerd"
                      maxLength={1000}
                    />
                  </div>

                  {/* Task Links - only for vak competencies */}
                  {formData.type === 'vak' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Gekoppelde Taken
                      </label>
                      {activeTasks.length > 0 ? (
                        <div className="border border-gray-200 rounded-md max-h-48 overflow-y-auto">
                          {activeTasks.map((task: TaskListItem) => (
                            <label
                              key={task.id}
                              className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={formData.taskIds.includes(task.id)}
                                onChange={() => handleTaskToggle(task.id)}
                                className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                              />
                              <span className="ml-3 text-sm text-gray-700">{task.name}</span>
                            </label>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">Geen actieve taken beschikbaar</p>
                      )}
                      <p className="mt-1 text-xs text-gray-500">
                        Selecteer de taken waaraan deze vakcompetentie is gekoppeld
                      </p>
                    </div>
                  )}
                </div>

                <div className="mt-6 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Annuleren
                  </button>
                  <button
                    type="submit"
                    disabled={createCompetency.isPending || updateCompetency.isPending}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                  >
                    {createCompetency.isPending || updateCompetency.isPending ? 'Opslaan...' : 'Opslaan'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModalState.isOpen && deleteModalState.competency && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="delete-modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500/75 transition-opacity"
              onClick={() => setDeleteModalState({ isOpen: false, competency: null, error: null })}
            ></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="relative z-10 inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              {deleteModalState.error?.code === 'CANNOT_DELETE' ? (
                // Blocked: competency in use
                <>
                  <div className="flex items-center mb-4">
                    <div className="flex-shrink-0 h-10 w-10 rounded-full bg-yellow-100 flex items-center justify-center">
                      <svg className="h-6 w-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                    <h3 className="ml-3 text-lg font-medium text-gray-900">
                      Competentie verwijderen niet mogelijk
                    </h3>
                  </div>
                  <p className="text-sm text-gray-500 mb-4">
                    De competentie "{deleteModalState.competency.name}" is in gebruik en kan niet worden verwijderd:
                  </p>
                  <ul className="text-sm text-gray-500 list-disc list-inside mb-4">
                    {deleteModalState.competency.employeeCount > 0 && (
                      <li>Toegewezen aan {deleteModalState.competency.employeeCount} medewerker(s)</li>
                    )}
                    {deleteModalState.competency.taskCount > 0 && (
                      <li>Gekoppeld aan {deleteModalState.competency.taskCount} ta(a)k(en)</li>
                    )}
                  </ul>
                  <p className="text-sm text-gray-500 mb-4">
                    U kunt de competentie wel inactief zetten.
                  </p>
                  <div className="flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => setDeleteModalState({ isOpen: false, competency: null, error: null })}
                      className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Sluiten
                    </button>
                    <button
                      type="button"
                      onClick={async () => {
                        if (deleteModalState.competency) {
                          await handleToggleActive(deleteModalState.competency.id);
                          setDeleteModalState({ isOpen: false, competency: null, error: null });
                        }
                      }}
                      disabled={toggleActive.isPending}
                      className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700 disabled:opacity-50"
                    >
                      {toggleActive.isPending ? 'Bezig...' : 'Inactief zetten'}
                    </button>
                  </div>
                </>
              ) : (
                // Normal delete confirmation
                <>
                  <div className="flex items-center mb-4">
                    <div className="flex-shrink-0 h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
                      <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </div>
                    <h3 className="ml-3 text-lg font-medium text-gray-900">
                      Competentie verwijderen
                    </h3>
                  </div>
                  <p className="text-sm text-gray-500 mb-4">
                    Weet u zeker dat u de competentie "{deleteModalState.competency.name}" wilt verwijderen?
                    Deze actie kan niet ongedaan worden gemaakt.
                  </p>
                  <div className="flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => setDeleteModalState({ isOpen: false, competency: null, error: null })}
                      className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Annuleren
                    </button>
                    <button
                      type="button"
                      onClick={handleConfirmDelete}
                      disabled={deleteCompetency.isPending}
                      className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
                    >
                      {deleteCompetency.isPending ? 'Verwijderen...' : 'Verwijderen'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Competencies List */}
      {filteredCompetencies.length > 0 ? (
        <div className="overflow-hidden border border-gray-200 rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Competentie
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Gekoppeld aan
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acties
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredCompetencies.map((comp) => (
                <tr key={comp.id} className={`hover:bg-gray-50 ${!comp.isActive ? 'opacity-60' : ''}`}>
                  <td className="px-6 py-4">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{comp.name}</div>
                      {comp.beschrijving && (
                        <div className="text-sm text-gray-500 truncate max-w-xs">{comp.beschrijving}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      comp.type === 'basis' ? 'bg-purple-100 text-purple-800' : 'bg-indigo-100 text-indigo-800'
                    }`}>
                      {comp.type === 'basis' ? 'Basis' : 'Vak'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {comp.type === 'basis' ? (
                      comp.employeeCount > 0 ? (
                        <span className="inline-flex items-center">
                          <svg className="mr-1 h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          {comp.employeeCount} medewerker(s)
                        </span>
                      ) : (
                        <span className="text-gray-400">Geen medewerkers</span>
                      )
                    ) : (
                      comp.linkedTasks.length > 0 ? (
                        <span className="inline-flex items-center">
                          <svg className="mr-1 h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                          </svg>
                          {comp.linkedTasks.length} ta(a)k(en)
                        </span>
                      ) : (
                        <span className="text-gray-400">Geen taken</span>
                      )
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => handleToggleActive(comp.id)}
                      disabled={toggleActive.isPending}
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium cursor-pointer ${
                        comp.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {comp.isActive ? 'Actief' : 'Inactief'}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleEdit(comp)}
                      className="text-blue-600 hover:text-blue-900 mr-4"
                    >
                      Bewerken
                    </button>
                    <button
                      onClick={() => handleDeleteClick(comp)}
                      className="text-red-600 hover:text-red-900"
                      disabled={deleteCompetency.isPending}
                    >
                      Verwijderen
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
          </svg>
          <h3 className="mt-4 text-lg font-medium text-gray-900">Geen competenties gevonden</h3>
          <p className="mt-2 text-sm text-gray-500">
            {searchQuery || typeFilter !== 'all' || statusFilter !== 'all'
              ? 'Pas de filters aan of voeg een nieuwe competentie toe.'
              : 'Begin met het toevoegen van competenties.'}
          </p>
        </div>
      )}

      {/* Summary */}
      <div className="mt-4 flex items-center justify-between text-sm text-gray-500">
        <div>
          Totaal: {filteredCompetencies.length} competenties
          {(typeFilter !== 'all' || statusFilter !== 'all') && ` (gefilterd van ${competencies?.length ?? 0})`}
        </div>
        <div className="flex gap-4">
          <span className="inline-flex items-center">
            <span className="inline-block w-3 h-3 rounded-full bg-purple-100 mr-1"></span>
            Basis: {competencies?.filter(c => c.type === 'basis').length ?? 0}
          </span>
          <span className="inline-flex items-center">
            <span className="inline-block w-3 h-3 rounded-full bg-indigo-100 mr-1"></span>
            Vak: {competencies?.filter(c => c.type === 'vak').length ?? 0}
          </span>
        </div>
      </div>
    </div>
  );
}
